import { FormControlLabel, Radio, RadioGroup } from '@mui/material';
import { FormikProvider, useFormik } from 'formik';
import React, { memo, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { audioApi, pdfApi, wordApi, taskApi, videoApi } from 'api';
import { imgApi } from 'api/img-api';
import { BREAKPOINTS } from 'constants/breakpoints';
import { useMediaQuery } from 'shared/hooks';
import { Exam, GapTask as GapTaskType, GapTaskTypes, GapWord, TaskKeysType, TaskType, WorkType } from 'shared/types';
import { gapTaskSchema } from 'shared/types/task';
import { TaskName } from 'shared/ui';
import Clue from 'shared/ui/Clue';

import TaskWrapper from '../TaskWrapper';
import InputPoint from 'Components/InputPoint/InputPoint';

export interface GapTaskFormikValues {
  text: string;
  subText?: string;
  id: string;
  indexNumber: number;
  isActive: boolean;
  createdAt: number;
  type: TaskType;
  hasAnswer: boolean;
  hasStar?: boolean;
  hasPhoto: boolean;
  sentence: string;
  gapWords: GapWord[];
  gapTaskType: GapTaskTypes;
  maxGrade: number | null;
}

export type GapTaskProps = GapTaskType & {
  exam: Exam;
  handleDelete: (id: string) => void;
  userType: string;
  handleChangeTaskByKey: (id: string, key: TaskKeysType, value: string | boolean | string[]) => void;
  currentICLId: string;
  updateICL: boolean;
  workType?: WorkType;
  index: number;
  points?: 0;
};

function GapTask({
  id,
  handleDelete,
  completedAt,
  text,
  subText,
  handleChangeTaskByKey,
  hasAnswer,
  hasStar,
  indexNumber,
  isActive,
  createdAt,
  type,
  currentICLId,
  updateICL,
  hasPhoto,
  deadline,
  audioUrl,
  videoUrl,
  pdfUrl,
  wordUrl,
  taskPhotos,
  answersPhotos,
  editedAnswerPhotos,
  workType,
  studentPdfUrl,
  studentWordUrl,
  sentence,
  gapWords,
  gapTaskType,
  exam,
  maxGrade,
  index,
}: GapTaskProps) {
  const { t } = useTranslation();
  const [taskId, setTaskId] = useState<string>('');
  const [isRemovingLoading, setIsRemovingLoading] = useState(false);
  const lg = useMediaQuery(BREAKPOINTS.lg);
  const isExam = exam?.type === WorkType.exam;

  const validateValues = (obj: Record<string, any>): Record<string, any> => {
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([_, v]) => v !== undefined)
        .map(([k, v]) => [k, v && typeof v === 'object' ? validateValues(v) : v]),
    );
  };

  useEffect(() => {
    const init = async () => {
      try {
        const task = await taskApi.getTask(currentICLId, id);

        if (task) {
          setTaskId(task.id);
        }
      } catch (error) {
        console.log(error, 'error init task');
      }
    };

    init(); // to check if task exists in Firestore
  }, []);

  const initialFormValues = {
    text: updateICL ? text.replace(/<br>/g, '\n') : '',
    subText: updateICL ? subText?.replace(/<br>/g, '\n') : '',
    id,
    indexNumber,
    isActive,
    createdAt,
    type,
    hasAnswer,
    hasStar,
    hasPhoto,
    sentence,
    gapWords,
    gapTaskType: gapTaskType || GapTaskTypes.letters,
    maxGrade: maxGrade || null,
  };

  const formValues = isExam ? { ...initialFormValues, points: 0 } : initialFormValues;

  const formik = useFormik<GapTaskType>({
    initialValues: formValues,
    validationSchema: gapTaskSchema,
    onSubmit: async value => {
      const values = validateValues(value) as GapTaskFormikValues;

      const textWithLB = values.text.replace(/\n/g, '<br>');
      const subTextWithLB = values?.subText?.replace(/\n/g, '<br>');

      const isTheSameSentence = values.sentence === sentence;

      const newWords = values.sentence.split(' ');

      const newGapWords = newWords.map(word => {
        const newId = uuidv4();
        let correctAnswer = '';
        let wordWithoutAnswer = '';

        if (values.gapTaskType === GapTaskTypes.letters && word.includes('_')) {
          const twoWordParts = word.split('_');
          correctAnswer = twoWordParts[1][0];
          const secondWordPartWithoutAnswer = twoWordParts[1].slice(1);
          wordWithoutAnswer = twoWordParts[0] + '_' + secondWordPartWithoutAnswer;
        }

        if (values.gapTaskType === GapTaskTypes.words && word.includes('_')) {
          correctAnswer = word.split('_')[1];
          wordWithoutAnswer = '_';
        }

        return { id: newId, word: wordWithoutAnswer || word, answer: '', correctAnswer } as GapWord;
      });

      const preparedGapWords = isTheSameSentence ? gapWords : newGapWords;

      if (!taskId) {
        await taskApi
          .addTask<GapTaskType>(currentICLId, {
            ...values,
            indexNumber,
            text: textWithLB,
            subText: subTextWithLB,
            gapWords: preparedGapWords,
            completedAt,
          })
          .then(id => {
            setTaskId(id);
            toast.success(t('Successfully added!'));

            return id;
          });
      } else {
        await taskApi.updateTask<GapTaskType>(currentICLId, {
          ...values,
          text: textWithLB,
          subText: subTextWithLB,
          gapWords: preparedGapWords,
          completedAt,
        });
        toast.success(t('Successfully edited!'));
      }
    },
  });

  const deleteTask = async () => {
    if (!taskId) {
      handleDelete(id);
    }

    if (updateICL || taskId) {
      handleDelete(id);

      setIsRemovingLoading(true);
      wordUrl && (await wordApi.removeWord(wordUrl, currentICLId, id));
      pdfUrl && (await pdfApi.removePdf(pdfUrl, currentICLId, id));
      studentWordUrl && (await wordApi.removeWord(studentWordUrl, currentICLId, id, true));
      studentPdfUrl && (await pdfApi.removePdf(studentPdfUrl, currentICLId, id, true));
      videoUrl && (await videoApi.removeVideo(videoUrl, currentICLId, id));
      audioUrl && (await audioApi.removeAudio(audioUrl, currentICLId, id));
      taskPhotos &&
        taskPhotos.forEach(async photo => await imgApi.removeImg(photo, currentICLId, id, taskPhotos, 'taskPhotos'));
      answersPhotos &&
        answersPhotos.forEach(
          async photo => await imgApi.removeImg(photo, currentICLId, id, answersPhotos, 'answersPhotos'),
        );
      editedAnswerPhotos &&
        editedAnswerPhotos.forEach(
          async photo => await imgApi.removeImg(photo, currentICLId, id, editedAnswerPhotos, 'editedAnswerPhotos'),
        );
      await taskApi.removeTask<GapTaskType>(currentICLId, id);
      setIsRemovingLoading(false);
    }
  };

  const handleChangeTaskByKeyWithFormik = (id: string, key: TaskKeysType, value: string | boolean | string[]) => {
    handleChangeTaskByKey(id, key, value);

    formik.setFieldValue(key, value);
  };

  return (
    <TaskWrapper
      id={id}
      handleDelete={deleteTask}
      completedAt={completedAt}
      handleChangeTaskByKey={handleChangeTaskByKeyWithFormik}
      hasAnswer={hasAnswer}
      hasStar={hasStar}
      save={formik.submitForm}
      formikProp={formik}
      updateICL={updateICL}
      isDeleteButtonDisabled={isRemovingLoading}
      hasPhoto={hasPhoto}
      deadline={deadline}
      isActive={isActive}
      currentICLId={currentICLId}
      audioUrl={audioUrl}
      remoteTaskId={taskId}
      videoUrl={videoUrl}
      pdfUrl={pdfUrl}
      wordUrl={wordUrl}
      taskPhotos={taskPhotos}
      workType={workType}
      studentPdfUrl={studentPdfUrl}
      studentWordUrl={studentWordUrl}
      index={index}
    >
      <FormikProvider value={formik}>
        <div className="flex flex-col !gap-4 lg:!gap-5">
          <TaskName />

          {isExam && (
            <InputPoint value={formik.values.points || 0} onChange={value => formik.setFieldValue('points', value)} />
          )}

          <div className="relative">
            <p className="!mb-0 text-[14px] font-bold uppercase lg:text-[18px]">{t('exercise heading')}:</p>

            <textarea
              value={formik.values.text}
              className={
                formik.errors.text
                  ? 'h-[70px] w-full border-b-2 border-none bg-[linear-gradient(to_right,_#f5f5f5_1px,_transparent_1px),_linear-gradient(to_left,_#f5f5f5_1px,_transparent_1px),_repeating-linear-gradient(#f5f5f5,_#f5f5f5_33px,_#FA6238_35px,_#97A0C3_31px,_#f5f5f5_35px)] bg-local pt-[8px] text-[18px] font-medium leading-[35px] placeholder:text-lg lg:text-[22px] lg:placeholder:text-[22px]'
                  : 'h-[70px] w-full border-b-2 border-none bg-[linear-gradient(to_right,_#f5f5f5_1px,_transparent_1px),_linear-gradient(to_left,_#f5f5f5_1px,_transparent_1px),_repeating-linear-gradient(#f5f5f5,_#f5f5f5_33px,_#242C2C_35px,_#97A0C3_31px,_#f5f5f5_35px)] bg-local pt-[8px] text-[18px] font-medium leading-[35px] placeholder:text-lg lg:text-[22px] lg:placeholder:text-[22px]'
              }
              onChange={event => formik.setFieldValue('text', event.target.value)}
              placeholder={t('enter task title')}
            />

            {!!formik.errors.text && (
              <p className="absolute left-0 top-14 !mb-0 inline-block h-fit text-sm font-medium text-red-600 lg:top-[60px]">
                {t(formik.errors.text)}
              </p>
            )}
          </div>

          <div className="relative">
            <p className="!mb-0 text-[14px] font-bold uppercase lg:text-[18px]">{t('exercise')}:</p>

            <textarea
              value={formik.values.subText}
              className={`h-[70px] w-full border-b-2 text-[18px] lg:text-[22px] ${formik.errors.subText ? 'border-red-600' : 'border-black'} border-none bg-[linear-gradient(to_right,_#f5f5f5_1px,_transparent_1px),_linear-gradient(to_left,_#f5f5f5_1px,_transparent_1px),_repeating-linear-gradient(#f5f5f5,_#f5f5f5_33px,_#242C2C_35px,_#97A0C3_31px,_#f5f5f5_35px)] bg-local pt-[8px] font-medium leading-[35px] placeholder:text-lg lg:placeholder:text-[22px]`}
              onChange={event => formik.setFieldValue('subText', event.target.value)}
              placeholder={t('enter task text')}
            />

            {!!formik.errors.subText && (
              <p className="absolute left-0 top-14 !mb-0 inline-block h-fit text-sm font-medium text-red-600 lg:top-[60px]">
                {t(formik.errors.subText)}
              </p>
            )}
          </div>

          {isExam && (
            <div>
              <div className="text-[14px] font-bold uppercase lg:text-[18px]">{t('maximum amount of points')}:</div>

              <input
                id="maxGrade"
                className={`w-full border-b text-[18px] font-medium placeholder:text-lg lg:text-[22px] lg:placeholder:text-[22px] ${formik.errors.maxGrade ? 'border-red-600' : 'border-black'} bg-transparent focus:outline-none`}
                value={formik.values.maxGrade}
                onChange={event => formik.setFieldValue('maxGrade', event.target.value)}
                type="number"
              />
            </div>
          )}

          <div>
            <p className="!mb-2 text-[14px] font-bold uppercase lg:text-[18px]">{t('kind of exercise')}:</p>

            <RadioGroup
              row={lg}
              value={formik.values.gapTaskType}
              onChange={event => formik.setFieldValue('gapTaskType', event.target.value)}
            >
              <FormControlLabel
                value={GapTaskTypes.letters}
                label={t('exercise with missed letters')}
                sx={{
                  mb: '12px',
                  mx: '0px',
                  '@media (min-width: 1024px)': {
                    mb: 0,
                  },
                  '& .MuiTypography-root': {
                    fontSize: 14,
                    fontFamily: 'Mulish',
                    fontWeight: 700,
                    lineHeight: '25px',
                    '@media (min-width: 1024px)': {
                      mr: '32px',
                      fontSize: 18,
                    },
                  },
                }}
                control={
                  <Radio
                    sx={{
                      p: 0,
                      mr: '10px',
                      color: '#4383F2',
                      '&.Mui-checked': {
                        color: '#4383F2',
                      },
                      fontSize: 14,
                    }}
                  />
                }
              />
              <FormControlLabel
                value={GapTaskTypes.words}
                label={t('exercise with missed words')}
                sx={{
                  mx: '0px',
                  '& .MuiTypography-root': {
                    fontSize: 14,
                    fontFamily: 'Mulish',
                    fontWeight: 700,
                    lineHeight: '25px',
                    '@media (min-width: 1024px)': {
                      fontSize: 18,
                    },
                  },
                }}
                control={
                  <Radio
                    sx={{
                      p: 0,
                      mr: '10px',
                      color: '#4383F2',
                      '&.Mui-checked': {
                        color: '#4383F2',
                      },
                    }}
                  />
                }
              />
            </RadioGroup>
          </div>

          <Clue text={formik.values.gapTaskType === GapTaskTypes.letters ? 'gapLettersHint' : 'gapWordsHint'} />

          <div className="relative">
            <div className="text-[14px] font-bold uppercase lg:text-[18px]">{t('content of exercise')}:</div>

            <textarea
              value={formik.values.sentence}
              className={
                formik.errors.sentence
                  ? 'h-[70px] w-full border-b-2 border-none bg-[linear-gradient(to_right,_#f5f5f5_1px,_transparent_1px),_linear-gradient(to_left,_#f5f5f5_1px,_transparent_1px),_repeating-linear-gradient(#f5f5f5,_#f5f5f5_33px,_#FA6238_35px,_#97A0C3_31px,_#f5f5f5_35px)] bg-local pt-[8px] text-[18px] font-medium leading-[35px] placeholder:text-lg lg:text-[22px] lg:placeholder:text-[22px]'
                  : 'h-[70px] w-full border-b-2 border-none bg-[linear-gradient(to_right,_#f5f5f5_1px,_transparent_1px),_linear-gradient(to_left,_#f5f5f5_1px,_transparent_1px),_repeating-linear-gradient(#f5f5f5,_#f5f5f5_33px,_#242C2C_35px,_#97A0C3_31px,_#f5f5f5_35px)] bg-local pt-[8px] text-[18px] font-medium leading-[35px] placeholder:text-lg lg:text-[22px] lg:placeholder:text-[22px]'
              }
              onChange={event => formik.setFieldValue('sentence', event.target.value)}
              placeholder={t('enter task text')}
            />

            {!!formik.errors.sentence && (
              <p className="absolute left-0 top-14 !mb-0 inline-block h-fit text-sm font-medium text-red-600 lg:top-[60px]">
                {t(formik.errors.sentence)}
              </p>
            )}
          </div>
        </div>
      </FormikProvider>
    </TaskWrapper>
  );
}

export default memo(GapTask);
